import { connect, FieldArray, FormikValues } from 'formik'
import { cloneDeep } from 'lodash'
import { Fragment, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { v4 } from 'uuid'

import Button from '@/components/Button'
import { Radio, Text } from '@/components/Form'
import { AddCircle, Delete } from '@/components/Icons'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { StyledCenterAlignedRow, StyledDivider } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import * as actions from '@/pages/SpaceAppBuilder/FeaturesPage/actions'
import BoltIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/BoltIcon'
import CursorTextIcon from '@/pages/SpaceAppBuilder/FeaturesPage/images/CursorTextIcon'
import DeleteConfirmationModal from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/DeleteConfirmationModal/DeleteConfirmationModal'
import { getAppBuilderFeatureData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { convertFeaturesToRelatedFeatures } from '@/pages/SpaceAppBuilder/FeaturesPage/utils'
import {
  ActionBlockDataType,
  ActionData,
  ActionTypes,
  App,
  Feature,
  RelatedFeature
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getFetchAppQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import { AddButton } from './ButtonBlock'
import CreateActionFeatureModal from './CreateActionFeatureModal/CreateActionFeatureModal'
import { InputTypes } from './InputText'
import messages from './messages'
import SaveOperationModal from './SaveOperationModal/SaveOperationModal'
import { BlockTitle } from './StyledComponents'

type ActionDataBuilderProps = {
  readOnly?: boolean
}

const Row = styled.div`
  display: flex;
`
const StyledText = styled(Text).withConfig({
  shouldForwardProp: (prop: any) => !['isFirstRow'].includes(prop)
})`
  && {
    &.form__field {
      margin-bottom: 0;
      margin-top: 0;
    }
    flex: 1;
    min-width: 140px;
    height: 30px;

    .field__error {
      display: none;
    }

    .field__label {
      margin-bottom: ${props => (props.isFirstRow ? '0.75rem' : '0.25rem')};
    }
  }
`
const EventAttributeRow = styled<any>(Row)`
  && {
    flex: 1;
    width: 100%;
    align-items: flex-start;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`
const ActionFeature = styled.div`
  border: 1px dashed #cccccc7d;
  border-radius: 0.25rem;
  width: 100%;
  padding: 10px;
  min-height: 40px;
  margin-top: 10px;
`
const StyledBoltIcon = styled(BoltIcon)`
  display: flex;
  margin-top: 12px;
  margin-right: 10px;
`
const StyledRow = styled.div`
  display: flex;
  flex: 1 0 auto;
  width: 100%;
  > div {
    padding-right: 10px;
  }
`
const StyledBlockTitle = styled(BlockTitle)`
  margin-bottom: 10px;
  margin-top: 10px;
`
const StyledBlockSubTitle = styled(BlockTitle)`
  margin-bottom: 10px;
  margin-top: 10px;
  font-size: 0.9rem;
`
const LinkContainer = styled.div`
  margin-right: auto;
  margin-top: 5px;
  margin-bottom: 5px;
  a {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    gap: 5px;
  }
`
const StyledVariableRow = styled(StyledRow)`
  justify-content: space-between;
  align-items: center;
`
const StyledInputVariable = styled.div`
  color: #858585;
  font-size: 0.8rem;
  align-items: center;
  display: flex;
`
const StyledInputVariableType = styled.div`
  font-size: 0.75rem;
  color: #858585;
  font-style: italic;
`
const StyledCursorTextIcon = styled(CursorTextIcon)`
  margin-right: 0.5rem;
`
const StyledInputVariableDivider = styled(StyledDivider)`
  margin: 0.25rem 0;
  top: 0;
`
const StyledDeleteIconButton = styled.button`
  background: transparent;
  outline: none;
  border: none;
  margin-left: 10px;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 10px;
  cursor: pointer;
`
const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  && {
    width: 200px;
    &.form__filter-select {
      margin-left: 10px;
      .field__wrapper .ui.fluid.dropdown {
        max-height: 38px;
        .menu {
          max-width: 250px;
        }
      }
    }
  }
`
const StyledButton = styled(Button)`
  margin-left: 0;
  margin-bottom: 0;
  margin-top: 0;
`
const StyledAddCircle = styled(AddCircle)`
  margin-right: 5px;
  color: #506e91;
`
const Description = styled.p`
  color: var(--wg-gray-border);
  font-size: 0.85rem;
`
const StyledList = styled.ul`
  font-size: 0.8rem;
  padding-left: 20px;
  list-style: square;
  li::marker {
    content: '✓ ';
  }
`

const ActionDataBuilder = ({
  formik,
  variables,
  selectedElement,
  readOnly = false
}: ActionDataBuilderProps & FormikValues) => {
  const intl = useIntl()
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()

  const { actionData } = formik.values.stepData.notifications[0]
  const { detail } = formik.values.stepData.notifications[0]
  const [featureModal, setFeatureModal] = useState({ isOpen: false })
  const [actionFeature, setActionFeature] = useState<Feature | undefined>()
  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId })
  })
  const featureFetchState = useAppSelector(getAppBuilderFeatureData)
  const [showSaveNotificationModal, setShowSaveNotificationModal] = useState(false)
  const location = useLocation()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  interface DeleteModalState {
    isOpen: boolean
    matchedActionFeatures: [] | Feature[]
  }
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState<DeleteModalState>({
    isOpen: false,
    matchedActionFeatures: []
  })

  useEffect(() => {
    if (actionData) {
      actionData.forEach((actionEvent: ActionData) => {
        const index = actionData.indexOf(actionEvent)
        formik.setFieldValue(
          `stepData.notifications[0].actionData[${index}].attributeName`,
          actionEvent.attributeName.replace(' ', '').replaceAll(/[^A-Za-z]/gm, '')
        )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData])

  useEffect(() => {
    if (featureFetchState.feature?.relatedFeatures) {
      const stepActionFeature = featureFetchState.feature?.relatedFeatures?.filter(
        (relatedFeature: RelatedFeature) => relatedFeature.stepId === selectedElement.id
      )
      if (stepActionFeature.length > 0) {
        const matchedActionFeature: Feature | undefined = appQueryResult?.data?.features?.find((feature: Feature) =>
          stepActionFeature.some(
            (stepActionFeatureItem: RelatedFeature) => feature.id === stepActionFeatureItem.featureId
          )
        )
        matchedActionFeature ? setActionFeature(matchedActionFeature) : setActionFeature(undefined)
      } else {
        setActionFeature(undefined)
      }
    } else {
      setActionFeature(undefined)
    }
  }, [featureFetchState.feature?.relatedFeatures, appQueryResult?.data?.features, selectedElement?.id])

  useEffect(() => {
    const actions = formik.values.stepData.notifications[0].actions?.map((action: ActionBlockDataType) => {
      if (action.type === ActionTypes.ActionSubmit) {
        return {
          ...action,
          data:
            actionData
              ?.filter(
                (actionEventData: ActionData) =>
                  (actionEventData.attributeName && actionEventData.attributeValue) ||
                  (actionEventData.attributeName && actionEventData.inputType === InputTypes.InputText)
              )
              ?.reduce(
                (acc: any, actionEventData: ActionData) => ({
                  ...acc,
                  [actionEventData.attributeName]: actionEventData.attributeValue
                }),
                {}
              ) || {}
        }
      } else return action
    })
    if (formik.values.stepData.notifications[0].actions) {
      formik.setFieldValue(`stepData.notifications[0].actions`, actions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData])

  // remove actionData item if the corresponding Input.Text item has been removed from detail
  useEffect(() => {
    if (detail) {
      const newActionData = cloneDeep(actionData)
      actionData?.forEach((actionDataItem: { id: any; inputType: string }, index: number) => {
        const detailContainsId = detail.some((detailItem: { id: any }) => detailItem.id === actionDataItem.id)
        if (!detailContainsId && actionDataItem.inputType === InputTypes.InputText) {
          newActionData.splice(index, 1)
          formik.setFieldValue(`stepData.notifications[0].actionData`, newActionData)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  const sortedActionData = (actionData: ActionData[]) => {
    return {
      inputTypeActionData: actionData?.filter((actionEventData: ActionData) => actionEventData.inputType),
      variableAttributeActionData: actionData?.filter((actionEventData: ActionData) => !actionEventData.inputType)
    }
  }
  const saveAndDisplayActionFeature = async () => {
    setShowSaveNotificationModal(true)
  }

  const shouldShowAddDataField = () => {
    return detail?.some((detailItem: { type: string }) => detailItem.type.includes('Input')) || variables?.length > 0
  }

  return (
    <div data-testid="actionDataBuilder">
      <ActionFeature>
        <StyledRow>
          <StyledBoltIcon />{' '}
          <StyledBlockTitle>'Submit' action {agentsFlagOn ? 'capability' : 'feature'}</StyledBlockTitle>{' '}
        </StyledRow>
        <Description>
          Action {agentsFlagOn ? 'capabilities' : 'features'} are used to further define the notification button(s)
          behaviour for submit interactions
        </Description>

        {shouldShowAddDataField() && (
          <>
            <StyledBlockSubTitle>{intl.formatMessage(messages.onSubmit)}</StyledBlockSubTitle>
            <StyledList>
              <li>
                <FormattedMessage {...messages.actionLabel} />
              </li>
              <li>
                <FormattedMessage {...messages.userID} />
              </li>
              <li>
                <FormattedMessage {...messages.userEmail} />
              </li>
            </StyledList>
            <FieldArray
              name="stepData.notifications[0].actionData"
              render={arrayHelpers => (
                <>
                  {sortedActionData(actionData).inputTypeActionData?.map((actionEventData: ActionData, i: number) => {
                    return (
                      <Fragment key={actionEventData.id}>
                        {i === 0 && <StyledInputVariableDivider />}
                        <StyledVariableRow>
                          <StyledInputVariable>
                            <StyledCursorTextIcon />
                            <span>
                              {actionEventData.attributeName || intl.formatMessage(messages.insertInputLabel)}
                            </span>
                          </StyledInputVariable>
                          <StyledInputVariableType>
                            {/* @ts-expect-error */}
                            {intl.formatMessage(messages[`${actionEventData.inputType}.type`])}
                          </StyledInputVariableType>
                        </StyledVariableRow>
                        <StyledInputVariableDivider />
                      </Fragment>
                    )
                  })}
                  {sortedActionData(actionData).variableAttributeActionData?.map((actionEventData: ActionData) => {
                    const index = actionData.indexOf(actionEventData)
                    return (
                      <EventAttributeRow key={actionEventData.id}>
                        <StyledText
                          disabled={readOnly}
                          isFirstRow={index === 0}
                          name={`stepData.notifications[0].actionData[${index}].attributeName`}
                          placeholder={intl.formatMessage(messages.fieldName)}
                        />
                        <StyledVariableAutoComplete
                          readOnly={readOnly}
                          placeholder={intl.formatMessage(messages.selectVariable)}
                          name={`stepData.notifications[0].actionData[${index}].attributeValue`}
                          variables={variables}
                          showVariableButton={false}
                        />
                        {!readOnly && (
                          <StyledDeleteIconButton onClick={() => arrayHelpers.remove(index)}>
                            <Delete title="Delete" />
                          </StyledDeleteIconButton>
                        )}
                      </EventAttributeRow>
                    )
                  })}
                  {variables?.length > 0 && !readOnly && (
                    <AddButton
                      message="addDataField"
                      onClick={() =>
                        arrayHelpers.push({
                          id: v4(),
                          attributeName: '',
                          attributeValue: ''
                        })
                      }
                    />
                  )}
                </>
              )}
            />
            <StyledBlockSubTitle>...to this action {agentsFlagOn ? 'capability' : 'feature'}:</StyledBlockSubTitle>
          </>
        )}

        {actionFeature ? (
          <StyledRow>
            <LinkContainer>
              {actionFeature.id && !readOnly && (
                <SaveOperationModal
                  onSave={() => {
                    formik.handleSubmit()
                    dispatch(actions.resetFeatureExecution())
                    if (actionFeature.id) {
                      history.push(location.pathname.replace(featureFetchState.feature.id, actionFeature.id))
                    }
                  }}
                  isOpen={showSaveNotificationModal}
                  onCancel={() => {
                    setShowSaveNotificationModal(false)
                  }}
                />
              )}

              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */}
              <a
                onClick={(e: any) => {
                  e.preventDefault()
                  readOnly && actionFeature.id
                    ? history.push(location.pathname.replace(featureFetchState.feature.id, actionFeature.id))
                    : saveAndDisplayActionFeature()
                }}
              >
                {actionFeature.name}
              </a>
            </LinkContainer>
            {!readOnly && (
              <StyledDeleteIconButton
                onClick={() => {
                  setDeleteConfirmationModal({ isOpen: true, matchedActionFeatures: [actionFeature] })
                }}
              >
                <Delete title={intl.formatMessage(messages.deleteActionFeature)} />
              </StyledDeleteIconButton>
            )}
            <DeleteConfirmationModal
              isOpen={deleteConfirmationModal.isOpen}
              deleteFrom={'panel-action-set'}
              onClose={() => setDeleteConfirmationModal({ isOpen: false, matchedActionFeatures: [] })}
              relatedFeatures={convertFeaturesToRelatedFeatures(deleteConfirmationModal.matchedActionFeatures)}
              agentsFlagOn={agentsFlagOn}
            />
          </StyledRow>
        ) : (
          !readOnly && (
            <StyledRow>
              <Radio name="stepData.notifications[0].shouldHaveActionFeature" required>
                <StyledButton color="secondary" onClick={() => setFeatureModal({ isOpen: true })}>
                  <StyledCenterAlignedRow>
                    <StyledAddCircle />
                    Build action {agentsFlagOn ? 'capability' : 'feature'}
                  </StyledCenterAlignedRow>
                </StyledButton>
              </Radio>
            </StyledRow>
          )
        )}
        <CreateActionFeatureModal
          notificationActions={formik.values.stepData.notifications[0].actions}
          actionData={actionData}
          selectedElement={selectedElement}
          isOpen={featureModal.isOpen}
          onClose={() => setFeatureModal({ isOpen: false })}
          agentsFlagOn={!!agentsFlagOn}
        />
      </ActionFeature>
    </div>
  )
}

export default connect(ActionDataBuilder)
